/**
 * Utility function to format date and time
 * Uses dayjs for reliable date/time formatting
 */

import dayjs from 'dayjs';

/**
 * Formats date and time with 12-hour format
 * Handles API format: "DD-MM-YYYY HH:MM" and converts to "DD MMM YYYY hh:mm A"
 * Assumes "DD-MM-YYYY HH:MM" format from API is already in the correct timezone, just formats it
 * For other formats, uses dayjs to parse and format
 * @param {string|Date|Object} dateString - Date string, Date object, or object with start_date/end_date
 * @returns {string} - Formatted date string: "DD MMM YYYY hh:mm A"
 */
export const formatDateTimeIST = (dateString) => {
  if (!dateString) return 'N/A';
  
  // Handle case where dateString is an object with start_date and end_date
  if (typeof dateString === 'object' && dateString !== null && !(dateString instanceof Date)) {
    // Prevent infinite recursion - if start_date or end_date are also objects, format them recursively
    if (dateString.start_date && dateString.end_date) {
      // Format as date range - handle nested objects recursively
      let start = dateString.start_date;
      let end = dateString.end_date;
      
      // If start_date or end_date are objects, format them recursively
      if (typeof start === 'object' && start !== null && !(start instanceof Date)) {
        start = formatDateTimeIST(start);
      } else {
        start = formatDateTimeIST(String(start));
      }
      
      if (typeof end === 'object' && end !== null && !(end instanceof Date)) {
        end = formatDateTimeIST(end);
      } else {
        end = formatDateTimeIST(String(end));
      }
      
      return `${start} to ${end}`;
    } else {
      return 'N/A';
    }
  }
  
  // Ensure we're working with a string
  if (typeof dateString !== 'string') {
    return 'N/A';
  }
  
  try {
    let date;
    
    // Check if the date is already in DD-MM-YYYY HH:MM format (from API)
    if (typeof dateString === 'string' && /^\d{2}-\d{2}-\d{4} \d{2}:\d{2}$/.test(dateString)) {
      // Parse DD-MM-YYYY HH:MM format
      // API sends this format, parse it directly without timezone conversion
      const [datePart, timePart] = dateString.split(' ');
      const [day, monthNum, year] = datePart.split('-');
      const [hours24, minutes] = timePart.split(':');
      
      // Create date using new Date constructor with local timezone
      // This ensures no timezone conversion happens
      const dateObj = new Date(
        parseInt(year, 10),
        parseInt(monthNum, 10) - 1, // month is 0-indexed
        parseInt(day, 10),
        parseInt(hours24, 10),
        parseInt(minutes, 10),
        0,
        0
      );
      
      date = dayjs(dateObj);
      
      if (!date.isValid()) {
        return 'N/A';
      }
    } else {
      // Try to parse as standard date string (ISO format, etc.)
      date = dayjs(dateString);
      
      if (!date.isValid()) {
        return 'N/A';
      }
    }
    
    // Format: DD MMM YYYY hh:mm A
    const formattedDate = date.format('DD MMM YYYY');
    const formattedTime = date.format('hh:mm A');
    
    return `${formattedDate} ${formattedTime}`;
  } catch (error) {
    console.error('Error formatting date:', error, dateString);
    return 'N/A';
  }
};

/**
 * Format date only (no time). Handles YYYY-MM-DD and other date strings.
 * @param {string|Date} dateString - Date string (e.g. "2026-02-10") or Date object
 * @returns {string} - Formatted date: "DD MMM YYYY" or "N/A"
 */
export const formatDateOnly = (dateString) => {
  if (!dateString) return 'N/A';
  const date = dayjs(dateString);
  return date.isValid() ? date.format('DD MMM YYYY') : 'N/A';
};

/**
 * Format a booking slot date for display: "2026-09-02" -> "02 Sep 2026".
 *
 * Unlike formatDateOnly this passes an unrecognised value through verbatim rather than showing
 * 'N/A'. Slot dates come from ApplicationVendor.slot_date, which is a CharField — a stored value
 * we cannot parse is still worth showing an operator, and returns '' when there is no slot at all
 * so the caller can render its own placeholder.
 *
 * @param {string} slotDate - ISO date string from the API (may be empty or non-ISO)
 * @returns {string} - "DD MMM YYYY", the raw value, or ''
 */
export const formatSlotDate = (slotDate) => {
  if (!slotDate) return '';
  const date = dayjs(String(slotDate).slice(0, 10));
  return date.isValid() ? date.format('DD MMM YYYY') : String(slotDate);
};

/**
 * Format a booking slot time for display: "09:30" -> "09:30 AM".
 * Passes an unrecognised value through verbatim, and returns '' when there is no time.
 *
 * @param {string} slotTime - "HH:MM" from the API (may be empty or non-numeric)
 * @returns {string} - "hh:mm A", the raw value, or ''
 */
export const formatSlotTime = (slotTime) => {
  if (!slotTime) return '';
  const raw = String(slotTime).trim();
  const [hours, minutes] = raw.split(':');
  const hour = parseInt(hours, 10);
  if (Number.isNaN(hour) || minutes === undefined) return raw;
  const suffix = hour >= 12 ? 'PM' : 'AM';
  return `${String(hour % 12 || 12).padStart(2, '0')}:${minutes.slice(0, 2)} ${suffix}`;
};

/**
 * Format a booking slot as one string in the SAME "DD MMM YYYY hh:mm A" shape that
 * formatDateTimeIST produces, so an appointment column reads identically to a Date & Time column
 * sitting beside it.
 *
 * Returns '' (not 'N/A') when there is no slot, leaving the placeholder to the caller. A slot with
 * a date but no time renders just the date.
 *
 * @param {string} slotDate - ISO date string from the API
 * @param {string} slotTime - "HH:MM" from the API
 * @returns {string} - e.g. "02 Sep 2026 09:30 AM", or ''
 */
export const formatSlotDateTime = (slotDate, slotTime) => {
  const date = formatSlotDate(slotDate);
  if (!date) return '';
  const time = formatSlotTime(slotTime);
  return time ? `${date} ${time}` : date;
};

